import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { AIChatConfirmationMessageActions } from "./AIChatConfirmationMessageActions";
import { AIChatConfirmationMessageItem } from "./AIChatConfirmationMessageItem";
import { AIFeedbackButtons } from "../AIFeedbackButtons";

/**
 * A structured confirmation bubble the assistant can send instead of plain text. Always renders as
 * an assistant message (left-aligned, no `role` prop) -- a confirmation prompt only ever comes from
 * the assistant side of a conversation.
 *
 * A molecule (not an atom): composes the Button atom (via its own `.Actions`/`.Feedback` parts, for
 * the optional footer), which is what earns it molecule status -- it isn't self-contained the way a
 * plain `SimpleChatMessage` bubble is.
 *
 * The bubble is a `gap-2` flex column and everything in it is `children` -- the heading is plain
 * text (it needs no component of its own), and the parts below cover the rest. This card has no
 * opinion on what goes in it, how many of each part there are, or on switching between states.
 *
 * The violet accent is this design system's "ai" concept, backed by three tokens: `color.ai` (the
 * checkmark), `color.ai-background` (outer bubble, a light tint), and `color.ai-muted` (item
 * pills, a slightly stronger tint) -- each with its own light/dark value, so this doesn't depend
 * on `color-mix()` against the background to stay correct in dark mode the way the plainer
 * `SimpleChatMessage` bubble still does.
 *
 * Three ready-made parts (own files, same as AnalyticsCard.Value/.Chart) cover the common cases:
 * `.Item` (a checkmark pill), `.Actions` (a button row, while the confirmation is still pending)
 * and `.Feedback` (the shared `AIFeedbackButtons` component itself -- only makes sense
 * once a decision has been made). A consumer moving a message from pending to resolved renders
 * different children (or a different message instance entirely, the way a real conversation
 * transcript works) rather than this component transforming itself internally.
 *
 * @example
 * ```tsx
 * <AIChatConfirmationMessage>
 *   I'll create the following reminder:
 *   <AIChatConfirmationMessage.Item>Follow-up call</AIChatConfirmationMessage.Item>
 *   <AIChatConfirmationMessage.Item>Chen family</AIChatConfirmationMessage.Item>
 *   <AIChatConfirmationMessage.Actions>
 *     <Button variant="link" size="sm" onClick={...}>{t("Cancel")}</Button>
 *   </AIChatConfirmationMessage.Actions>
 * </AIChatConfirmationMessage>
 * ```
 */
export interface AIChatConfirmationMessageProps {
  className?: string;
  /**
   * The bubble's contents: heading text as a plain child, then any number of `.Item`s, and
   * optionally an `.Actions` or `.Feedback` footer.
   */
  children?: ReactNode;
}

const AIChatConfirmationMessageRoot = ({ className, children }: AIChatConfirmationMessageProps) => (
  <div data-slot="ai-chat-confirmation-message" className={cn("flex justify-start", className)}>
    <div className="flex max-w-[85%] flex-col gap-2 rounded-2xl bg-ai-background px-4 py-3 text-sm leading-5 text-foreground">
      {children}
    </div>
  </div>
);

export const AIChatConfirmationMessage = Object.assign(AIChatConfirmationMessageRoot, {
  Item: AIChatConfirmationMessageItem,
  Actions: AIChatConfirmationMessageActions,
  Feedback: AIFeedbackButtons,
});
